import React, { useState, useEffect } from 'react';
import './MySchedule.css';
import html2pdf from 'html2pdf.js';
import InviteFriends from './InviteFriends';
import SharableLink from './SharableLink';
import { fetchPicks as v2FetchPicks, listGroups as v2ListGroups, isArtOption } from './v2Api';

// Picks with no published times still have to land somewhere in a date-grouped
// schedule. They used to surface in the Art List by accident (see splitPicks);
// this key keeps them visible under their own heading instead of vanishing.
export const UNSCHEDULED = '__unscheduled__';

const toEventRow = (p) => ({
  uid: p.id,
  title: p.title || p.id,
  description: p.description || '',
  location_name: p.host || '',
  location_string: p.address || '',
  my_rating: p.my_rating || 0,
  my_vote: p.my_vote || null,
  rating: Math.round((p.my_rating || 0) / 10 * 10),
  occurrence_set: (p.availability?.windows || []).map(w => ({
    start_time: w.from,
    end_time: w.until,
  })),
  images: (p.pictures || []).map(url => ({ thumbnail_url: url })),
  type: 'event',
});

const toArtRow = (p) => ({
  uid: p.id,
  name: p.title || p.id,
  description: p.description || '',
  location_string: p.address || '',
  my_rating: p.my_rating || 0,
  my_vote: p.my_vote || null,
  rating: Math.round((p.my_rating || 0) / 10 * 10),
  location: p.location,
  unplaced: p.unplaced ?? false,
  type: 'art',
});

// Split the picks feed into the two rendered sections.
//
// The discriminator is the tag, never `availability.always` — which is what this
// used to key on, and which misfiles in BOTH directions:
//
//   • ART-SCHED gives art real time windows, flipping `always` true→false. Every
//     favourited art piece would slide into the Events list and the Art List
//     would render empty.
//   • An event with an empty occurrence_set is `always: true`, so it already
//     rendered as an art piece, GIS-coordinates column and all.
//
// Art keeps ignoring its windows here: the Art List is a place list, and
// favourited art ignoring the schedule is the shipped rule (ART-SCHED-4).
export function splitPicks(picks) {
  const events = [];
  const art = [];
  (picks || []).forEach(p => (isArtOption(p) ? art : events).push(p));
  return { events: events.map(toEventRow), art: art.map(toArtRow) };
}

const MySchedule = ({ user, embargoConfig }) => {
  const [events, setEvents] = useState([]);
  const [art, setArt] = useState([]);
  const [loading, setLoading] = useState(true);
  const [year, setYear] = useState(null);
  const [groups, setGroups] = useState([]);
  const [includeGroups, setIncludeGroups] = useState(true);
  const [includeOnlyExciting, setIncludeOnlyExciting] = useState(false);
  const domain = window.location.hostname;
  const today = new Date();

  const embargoCampsDate = embargoConfig?.embargoCampsDate ? new Date(embargoConfig.embargoCampsDate) : null;
  const embargoArtDate = embargoConfig?.embargoArtDate ? new Date(embargoConfig.embargoArtDate) : null;
  const isEmbargoActiveForEvents = embargoCampsDate ? (today < embargoCampsDate && today.getFullYear() === embargoCampsDate.getFullYear()) : false;
  const isEmbargoActiveForArt = embargoArtDate ? (today < embargoArtDate && today.getFullYear() === embargoArtDate.getFullYear()) : false;

  useEffect(() => {
    const fetchGroups = async () => {
      if (user) {
        try {
          const { groups } = await v2ListGroups(user);
          setGroups((groups || []).map(g => g.name));
        } catch (error) {
          console.error('Error fetching groups (v2):', error);
        }
      }
    };

    const fetchEvents = async () => {
      if (user) {
        try {
          const data = await v2FetchPicks(user);
          // data.picks is [{id, title, host, availability, my_vote, my_rating, address, pictures, tags}]
          const { events, art } = splitPicks(data.picks);
          setEvents(events);
          setArt(art);
          // Scan for the first event that actually carries a time — events[0]
          // can legitimately have an empty occurrence_set.
          const dated = events.find(e => e.occurrence_set?.[0]?.start_time);
          setYear(dated ? new Date(dated.occurrence_set[0].start_time).getFullYear() : 2026);
          setLoading(false);
        } catch (error) {
          console.error('Error fetching picks (v2):', error);
          setLoading(false);
        }
      }
    };

    fetchGroups();
    fetchEvents();
  }, [user]);



  const formatTime = (time) => {
    if (!time) return '';
    const date = new Date(time);
    if (isNaN(date.getTime())) return '';
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (date) => {
    const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(date).toLocaleDateString(undefined, options);
  };

  const groupEventsByDate = (events) => {
    const groupedEvents = {};

    events.forEach(event => {
      if (event.occurrence_set.length === 0) {
        // No published times — it has no date to sit under, but it is still a
        // pick the user made, so it gets its own bucket rather than vanishing.
        if (!groupedEvents[UNSCHEDULED]) {
          groupedEvents[UNSCHEDULED] = [];
        }
        groupedEvents[UNSCHEDULED].push({ ...event, start: null, end: null });
        return;
      }
      event.occurrence_set.forEach(occurrence => {
        const date = new Date(occurrence.start_time).toLocaleDateString();
        if (!groupedEvents[date]) {
          groupedEvents[date] = [];
        }
        groupedEvents[date].push({
          ...event,
          ...occurrence,
          start: occurrence.start_time,
          end: occurrence.end_time
        });
      });
    });

    // Real dates first, in order; the undated bucket always last.
    return Object.keys(groupedEvents)
      .sort((a, b) => {
        if (a === UNSCHEDULED) return 1;
        if (b === UNSCHEDULED) return -1;
        return a.localeCompare(b);
      })
      .reduce((acc, date) => {
        acc[date] = groupedEvents[date];
        return acc;
      }, {});
  };

  const handlePrint = () => {
    document.getElementById('button-container').style.display = 'none';
    window.print();
    document.getElementById('button-container').style.display = 'block';
  };

  const handleSaveToPDF = () => {
    document.getElementById('button-container').style.display = 'none';
    const element = document.getElementById('schedule-content');
    const opt = {
      margin: 0.6,
      filename: `Burning_Man_${year}_My_Picks.pdf`,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2 },
      jsPDF: { unit: 'in', format: 'letter', orientation: 'portrait' }
    };

    // Apply print styles for PDF generation
    const style = document.createElement('style');
    style.innerHTML = `
      .my-schedule h1 {
        font-size: 18px;
      }
      .my-schedule p {
        font-size: 14px;
      }
      .schedule-table h2 {
        font-size: 16px;
        font-weight: bold;
        text-align: left;
        margin-bottom: 10px;
      }
      .schedule-table th, .schedule-table td {
        font-size: 12px;
        padding: 4px; /* Reduced padding for print/PDF */
      }
      .schedule-table table {
        width: 100%;
      }
      .created-at {
        text-align: center;
        font-size: 12px;
        margin-top: 20px;
        visibility: visible; /* Ensure visibility when exporting */
        display:block;
      }

      .schedule-table tr:nth-child(even) {
        background-color: white !important;
      }

      .invite-friends-container, .dont-print{
        display: none;
      }
      
    `;
    document.head.appendChild(style);

    html2pdf().set(opt).from(element).save().then(() => {
      document.head.removeChild(style); // Clean up the style tag after PDF generation
      document.getElementById('button-container').style.display = 'block';
    });
  };

  const handleCheckboxChange = (e) => {
    setIncludeGroups(e.target.checked);
  };

  const handleExcitingCheckboxChange = (e) => {
    setIncludeOnlyExciting(e.target.checked);
  };

  const groupedEvents = groupEventsByDate(
    includeGroups ? events : events.filter(event => event.my_rating > 0)
  );

  const filteredArt = includeGroups ? art : art.filter(a => a.my_rating > 0);

  // Filter for exciting events if checkbox is checked — favorites only (my_vote === 'favorite')
  const excitingEvents = includeOnlyExciting ?
    Object.keys(groupedEvents).reduce((acc, date) => {
      const excitingEventsForDate = groupedEvents[date].filter(event => event.my_vote === 'favorite');
      if (excitingEventsForDate.length > 0) {
        acc[date] = excitingEventsForDate;
      }
      return acc;
    }, {}) : groupedEvents;

  const excitingArt = includeOnlyExciting ?
    filteredArt.filter(a => a.my_vote === 'favorite') : filteredArt;

  if (loading) {
    return <div>Loading...</div>;
  }

  if (events.length === 0 && art.length === 0) {
    return (
      <div className="my-schedule">
        <h1>Burning Man {year} — My Picks</h1>
        <p>Your picks based on your groups <strong>({groups.length > 0 ? groups.join(', ') : 'None'})</strong> and personal preferences</p>
        <p>You have not selected any events or art this year yet</p>
        <div className="button-container">
          <button className="active-button" onClick={() => window.location.href='/options'}>Explore events</button>
        </div>
      </div>
    );
  }

  return (
    <div className="my-schedule">
      <div id="schedule-content">
        <h1>Burning Man {year} — My Picks</h1>
        <p>
          {includeGroups ? 
            <>Your picks based on your groups <strong>({groups.length > 0 ? groups.join(', ') : 'None'})</strong> and personal preferences</>
            : 'Your picks include only your personal preferences'}
        </p>
        
        <div className="dont-print">
          <div className="checkbox-container">
            {groups.length > 0 && (
              <label>
                <input
                  type="checkbox"
                  checked={includeGroups}
                  onChange={handleCheckboxChange}
                />
                Include picks from my groups ({groups.join(', ')})
              </label>
            )}
            
            <label>
              <input
                type="checkbox"
                checked={includeOnlyExciting}
                onChange={handleExcitingCheckboxChange}
              />
              Include only exciting events
            </label>
          </div>
        </div>

        <div className="dont-print">
          <SharableLink user={user} includeGroups={includeGroups} includeOnlyExciting={includeOnlyExciting} />
        </div>
        
        <div id="button-container" className="button-container">
          <button onClick={handlePrint} className="print-button">🖨️ Print</button>
          <button onClick={handleSaveToPDF} className="pdf-button">💾 Save PDF file</button>
        </div>

        {(isEmbargoActiveForEvents || isEmbargoActiveForArt) && (
          <p>
            Per Burning Man API Policy, 
            {isEmbargoActiveForEvents && isEmbargoActiveForArt ? 
              `event and art locations will be visible on ${embargoCampsDate.toLocaleDateString()} and ${embargoArtDate.toLocaleDateString()} respectively.` 
              : isEmbargoActiveForEvents ? 
                `event locations will be visible on ${embargoCampsDate.toLocaleDateString()}.`
                : `art locations will be visible on ${embargoArtDate.toLocaleDateString()}.`
            }
          </p>
        )}

        {events.length === 0 ? (
          <p>You have not selected any events yet</p>
        ) : (
          Object.keys(excitingEvents).map(date => (
            <div key={date} className="schedule-table">
              <h2>{date === UNSCHEDULED ? 'No published time' : formatDate(date)}</h2>
              <table>
                <thead>
                  <tr>
                    <th>Time</th>
                    <th>Rating</th>
                    {!isEmbargoActiveForEvents && <th>Location</th>}
                    <th>Title</th>
                    <th>Description</th>
                    <th>Camp Name</th>
                  </tr>
                </thead>
                <tbody>
                  {excitingEvents[date]
                    .sort((a, b) => new Date(a.start) - new Date(b.start))
                    .map((event, index) => {
                      const isFavorite = event.my_rating === 10;
                      return (
                        <tr key={index} className={isFavorite ? 'bold-row' : ''}>
                          <td className='time-cell'>{event.start ? `${formatTime(event.start)} - ${formatTime(event.end)}` : '—'}</td>
                          <td>
                            {event.rating}
                            {isFavorite 
                              ? ' ⭐'
                              : event.my_rating
                                ? <strong> (you:{event.my_rating})</strong>
                                : ''
                            }
                          </td>
                          {!isEmbargoActiveForEvents && <td>{event.location_string}</td>}
                          <td>{event.title}</td>
                          <td>{event.description}</td>
                          <td>{event.location_name}</td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>
          ))
        )}

        {excitingArt.length > 0 && (
          <div className="schedule-table">
            <h2>Art List</h2>
            <table>
              <thead>
                <tr>
                  <th>Rating</th>
                  <th>Name</th>
                  <th>Description</th>
                  {!isEmbargoActiveForArt && <th>Location (GIS Coordinates)</th>}
                </tr>
              </thead>
              <tbody>
                {excitingArt
                  .sort((a, b) => b.rating - a.rating)
                  .map((a, index) => {
                    const isFavorite = a.my_rating === 10;
                    return (
                      <tr key={index} className={isFavorite ? 'bold-row' : ''}>
                        <td>
                          {a.rating}
                          {isFavorite 
                            ? ' ⭐'
                            : a.my_rating
                              ? <strong> (you:{a.my_rating})</strong>
                              : ''
                          }
                        </td>
                        <td>{a.name}</td>
                        <td>{a.description}</td>
                        {!isEmbargoActiveForArt && <td>{a.unplaced ? 'Location TBD' : (a.location_string + (a.location ? ` (${a.location.gps_latitude}, ${a.location.gps_longitude})` : ''))}</td>}
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
        )}

        <div className="created-at">
          Created at <a href={`https://${domain}`}>{domain}</a>
        </div>

        <div className="dont-print">
          <SharableLink user={user} />
        </div>

        <InviteFriends user={user} active={true} />
      </div>
    </div>
  );
};

export default MySchedule;
